import { replaceTemplate } from '@china-difi/lib/template';
import { getLanguage, http, location } from 'cdui-js';
import i18n from '../../../i18n';
import { routeReplace } from '../../../lib/browser';
import { userState } from '../../../stores/user';
import { appendDepositAppQuery, getDepositEntry } from '../deposit/h5-deposit/appBridge';
import {
  accountNumberOf,
  accountSubUserId,
  isSecuritiesAccount,
  overviewAccountsOf,
} from '../deposit/h5-deposit/shared';

export type BankCard = {
  rowId: string;
  bankCode: string;
  bankName: string;
  displayName: string;
  bankAccountNum: string;
  bankAccountName: string;
  currency: string;
  /** 卡片底部标签；有值时优先于 currencyLabel(currency) */
  tagText?: string;
  pending: boolean;
  bankID: string;
};

export type CountryOption = {
  code: string;
  label: string;
  letter: string;
};

export type DictOption = {
  value: string;
  label: string;
};

const DETAIL_KEY = 'h5-bank-account-item';
const FORM_BLOCK_KEY = 'h5-bank-account-block-form';

let marketBankCache: any[] = [];
let bankListCache: BankCard[] = [];
let bankListLoaded = false;

export const setMarketBankCache = (list: any[]) => {
  marketBankCache = Array.isArray(list) ? list : [];
};

export const getMarketBankCache = () => marketBankCache;

export const setBankListCache = (rows: BankCard[]) => {
  bankListCache = rows;
  bankListLoaded = true;
};

export const getBankListCache = () => bankListCache;

export const bankListHasLoaded = () => bankListLoaded;

export const bankRoot = () =>
  location.path.indexOf('/h5/bank-accounts') === 0 ? '/h5/bank-accounts' : '/bank-list';

/** 站内跳转丢掉的 query。`_t` 是开发缓存戳，不往下传 */
const DROP_QUERY = new Set(['data', 'id', '_t']);

const keptQuery = () => {
  const params = new URLSearchParams();
  try {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!key || !value || DROP_QUERY.has(key)) return;
      params.set(key, value);
    });
  } catch {
    // 地址栏读不到时，仍用首屏快照补 theme / lang / 安全区
  }
  appendDepositAppQuery(params);
  DROP_QUERY.forEach((key) => params.delete(key));
  return params;
};

export const listUrl = () => {
  const query = keptQuery().toString();
  return query ? `${bankRoot()}?${query}` : bankRoot();
};

export const detailUrl = (id: string) => {
  const params = keptQuery();
  if (id) params.set('id', id);
  const query = params.toString();
  return query ? `${bankRoot()}/detail?${query}` : `${bankRoot()}/detail`;
};

export const addUrl = () => {
  const query = keptQuery().toString();
  return query ? `${bankRoot()}/add?${query}` : `${bankRoot()}/add`;
};

export const successUrl = () => {
  const query = keptQuery().toString();
  return query ? `${bankRoot()}/add-success?${query}` : `${bankRoot()}/add-success`;
};

export const pendingListUrl = () => {
  const query = keptQuery().toString();
  return query ? `${bankRoot()}/pending-list?${query}` : `${bankRoot()}/pending-list`;
};

export const blockFormReturn = () => {
  sessionStorage.setItem(FORM_BLOCK_KEY, '1');
};

export const shouldLeaveForm = () => sessionStorage.getItem(FORM_BLOCK_KEY) === '1';

export const clearFormBlock = () => {
  sessionStorage.removeItem(FORM_BLOCK_KEY);
};

/** 成功后把当前记录换成列表，再进成功页，返回键不要回到已填表单 */
export const openAddSuccess = () => {
  blockFormReturn();
  window.history.replaceState(window.history.state, '', listUrl());
  location.routeTo(successUrl());
};

export const finishToList = () => {
  clearFormBlock();
  routeReplace(listUrl());
};

const LIST_KEYS = ['list', 'result', 'records', 'rows', 'items', 'banks', 'data'];

export const unwrapList = (res: any, depth = 0): any[] => {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== 'object' || depth > 3) return [];
  for (const key of LIST_KEYS) {
    if (res[key] == null) continue;
    const value = res[key];
    if (Array.isArray(value)) return value;
    const nested = unwrapList(value, depth + 1);
    if (nested.length) return nested;
  }
  return [];
};

export const dash = (value?: string | null) => {
  const text = String(value ?? '').trim();
  return text || '-';
};

export const descKey = () => {
  const lang = String(getLanguage() || 'zh-CN').trim();
  if (lang === 'en' || lang === 'en-US' || lang.startsWith('en-')) return 'enDesc';
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant' || lang.includes('Hant')) return 'tcDesc';
  return 'scDesc';
};

const nameField = () => {
  const lang = String(getLanguage() || 'zh-CN').trim();
  if (lang === 'en' || lang === 'en-US' || lang.startsWith('en-')) return 'bankNameEN';
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant') return 'bankNameTC';
  return 'bankNameSC';
};

/** 市场银行字典按语言取名，没有再退回接口 bankName */
export const resolveBankName = (row: any, fallback = '') => {
  const code = String(row?.bankCode ?? '');
  const hit = code ? marketBankCache.find((item) => String(item?.bankCode) === code) : null;
  const source = hit || row || {};
  const named = String(source[nameField()] || '').trim();
  return named || String(row?.bankName || source.bankName || '').trim() || fallback;
};

const LOGO_COLORS = ['#a61f23', '#cc0000', '#1b5299', '#006d3c', '#7b3f00', '#4a235a'];

export const logoChar = (name: string) => {
  const text = String(name || '').trim();
  return (text || i18n.BankList.LogoFallbackChar).charAt(0);
};

export const logoColor = (name: string) => {
  const ch = logoChar(name);
  return LOGO_COLORS[ch.charCodeAt(0) % LOGO_COLORS.length];
};

export const currencyLabel = (currency?: string | null) => {
  const code = String(currency ?? '').trim();
  if (!code) return '';
  if (code.toUpperCase() === 'M') return i18n.BankList.CurrencyAll;
  return replaceTemplate(i18n.BankList.CurrencySingle, { code: code.toUpperCase() });
};

export const isPending = (row: any) => String(row?.approveStatus || '').toUpperCase() === 'PENDING';

export const rowIdOf = (row: any, index: number) => {
  const code = String(row?.bankCode || '');
  const account = String(row?.bankAccountNum || '');
  if (isPending(row)) return `pending:${code}:${account}:${index}`;
  if (account) return `${code}_${account}`;
  return String(row?.bankID ?? index);
};

export const toBankCard = (row: any, index = 0): BankCard => {
  const bankCode = String(row?.bankCode || '');
  const bankName = String(row?.bankName || '');
  return {
    rowId: row?.rowId ? String(row.rowId) : rowIdOf(row, index),
    bankCode,
    bankName,
    displayName: resolveBankName(row),
    bankAccountNum: String(row?.bankAccountNum || ''),
    bankAccountName: String(row?.bankAccountName || ''),
    currency: String(row?.currency || ''),
    pending: row?.pending != null ? Boolean(row.pending) : isPending(row),
    bankID: row?.bankID == null || row?.bankID === '' ? '' : String(row.bankID),
  };
};

export const saveDetailItem = (item: BankCard) => {
  sessionStorage.setItem(DETAIL_KEY, JSON.stringify(item));
};

export const readDetailItem = (): BankCard | null => {
  const query = (location.query || {}) as Record<string, string>;
  if (query.data) {
    try {
      return toBankCard(JSON.parse(query.data));
    } catch {
      try {
        return toBankCard(JSON.parse(decodeURIComponent(query.data)));
      } catch {
        return null;
      }
    }
  }
  if (query.id) {
    const hit = bankListCache.find((item) => item.rowId === query.id);
    if (hit) return hit;
  }
  try {
    const raw = sessionStorage.getItem(DETAIL_KEY);
    if (!raw) return null;
    return toBankCard(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const refreshCardNames = (rows: BankCard[]) =>
  rows.map((item) => ({ ...item, displayName: resolveBankName(item) }));

export const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'string') {
    const text = error.replace(/^\s*\d+\s*/, '').trim();
    return text || fallback;
  }
  const message = (error as { message?: unknown })?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  return fallback;
};

export const isBizCode = (error: unknown, code: string) => {
  const raw = typeof error === 'string' ? error : String((error as { code?: unknown })?.code ?? '');
  return raw.trim().startsWith(code);
};

export const fetchBoundBanks = () => http.get('/2go/user/banks');

export const fetchMarketBanks = () => http.get('/2go/market/banks');

/** 添加银行卡审批中列表 */
export const fetchPendingBankApplies = () => http.get('/user/bankAddApply/pending');

const accountTypeTag = (value: string) => {
  const code = String(value || '').trim().toUpperCase();
  if (code === 'HKD') return i18n.BankList.TypeHkd;
  if (code === 'USD') return i18n.BankList.TypeUsd;
  if (code === 'CNY') return i18n.BankList.TypeCny;
  if (code === 'INTEGRATED') return i18n.BankList.TypeIntegrated;
  return '';
};

/** 申请单底部标签：账户类型 / 币种 */
export const pendingApplyTag = (row: any) => {
  const type = String(row?.bankAccountType || row?.accountType || '').trim();
  const typeLabel = accountTypeTag(type);
  if (type && type.toUpperCase() !== 'INTEGRATED' && typeLabel) return typeLabel;
  const currencies = String(row?.currencies || row?.currency || '')
    .split(/[,，、\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (currencies.length) {
    const labels = currencies.map((code) => accountTypeTag(code) || currencyLabel(code)).filter(Boolean);
    if (labels.length) return labels.join('、');
  }
  if (typeLabel) return typeLabel;
  return currencyLabel(row?.currency);
};

/** 审批列表行 → 卡片（强制 pending，兼容 bankAccountNo 等字段） */
export const toPendingBankCard = (row: any, index = 0): BankCard => {
  const normalized = {
    ...row,
    bankAccountNum: String(row?.bankAccountNum || row?.bankAccountNo || ''),
    currency: String(row?.currency || row?.currencies || row?.bankAccountType || ''),
    approveStatus: row?.approveStatus || 'PENDING',
    pending: true,
  };
  const card = toBankCard(normalized, index);
  return {
    ...card,
    pending: true,
    tagText: pendingApplyTag(row) || undefined,
    rowId: card.rowId.startsWith('pending:') ? card.rowId : `pending:${card.rowId}:${index}`,
  };
};

export const FALLBACK_ACCOUNT_TYPES = (): DictOption[] => [
  { value: 'HKD', label: i18n.BankList.TypeHkd },
  { value: 'USD', label: i18n.BankList.TypeUsd },
  { value: 'CNY', label: i18n.BankList.TypeCny },
  { value: 'INTEGRATED', label: i18n.BankList.TypeIntegrated },
];

const accountTypeLabel = (value: string) => {
  if (value === 'HKD') return i18n.BankList.TypeHkd;
  if (value === 'USD') return i18n.BankList.TypeUsd;
  if (value === 'CNY') return i18n.BankList.TypeCny;
  if (value === 'INTEGRATED') return i18n.BankList.TypeIntegrated;
  return '';
};

export const mapAccountTypes = (list: any[]): DictOption[] => {
  if (!Array.isArray(list) || !list.length) return FALLBACK_ACCOUNT_TYPES();
  const key = descKey();
  const rows = list
    .map((item) => {
      const value = String(item?.code ?? item?.value ?? '').trim();
      if (!value) return null;
      const label = String(accountTypeLabel(value) || item?.[key] || item?.label || value).trim();
      return { value, label };
    })
    .filter(Boolean) as DictOption[];
  return rows.length ? rows : FALLBACK_ACCOUNT_TYPES();
};

export const mapBankOptions = (list: any[]): DictOption[] => {
  if (!Array.isArray(list)) return [];
  const key = descKey();
  return list
    .map((item) => {
      const value = String(item?.code ?? item?.value ?? item?.bankCode ?? '').trim();
      if (!value) return null;
      const fallback = String(item?.[key] || item?.label || item?.bankName || value).trim();
      return { value, label: resolveBankName({ bankCode: value, bankName: fallback }, fallback) };
    })
    .filter(Boolean) as DictOption[];
};

const FALLBACK_COUNTRIES: { code: string; scDesc: string; tcDesc: string; enDesc: string }[] = [
  { code: 'AU', scDesc: '澳大利亚', tcDesc: '澳大利亞', enDesc: 'Australia' },
  { code: 'CA', scDesc: '加拿大', tcDesc: '加拿大', enDesc: 'Canada' },
  { code: 'CN', scDesc: '中国', tcDesc: '中國', enDesc: 'China' },
  { code: 'FR', scDesc: '法国', tcDesc: '法國', enDesc: 'France' },
  { code: 'DE', scDesc: '德国', tcDesc: '德國', enDesc: 'Germany' },
  { code: 'HK', scDesc: '中国香港', tcDesc: '中國香港', enDesc: 'Hong Kong' },
  { code: 'JP', scDesc: '日本', tcDesc: '日本', enDesc: 'Japan' },
  { code: 'KR', scDesc: '韩国', tcDesc: '韓國', enDesc: 'Korea' },
  { code: 'MO', scDesc: '中国澳门', tcDesc: '中國澳門', enDesc: 'Macao' },
  { code: 'MY', scDesc: '马来西亚', tcDesc: '馬來西亞', enDesc: 'Malaysia' },
  { code: 'NZ', scDesc: '新西兰', tcDesc: '新西蘭', enDesc: 'New Zealand' },
  { code: 'SG', scDesc: '新加坡', tcDesc: '新加坡', enDesc: 'Singapore' },
  { code: 'TW', scDesc: '中国台湾', tcDesc: '中國台灣', enDesc: 'Taiwan' },
  { code: 'TH', scDesc: '泰国', tcDesc: '泰國', enDesc: 'Thailand' },
  { code: 'GB', scDesc: '英国', tcDesc: '英國', enDesc: 'United Kingdom' },
  { code: 'US', scDesc: '美国', tcDesc: '美國', enDesc: 'United States' },
  { code: 'VN', scDesc: '越南', tcDesc: '越南', enDesc: 'Vietnam' },
];

const toCountry = (row: any): CountryOption | null => {
  const code = String(row?.code ?? row?.value ?? '').trim();
  if (!code) return null;
  const key = descKey();
  const label = String(row?.[key] || row?.scDesc || row?.label || code).trim();
  const letter = String(row?.enDesc || label || code)
    .trim()
    .charAt(0)
    .toUpperCase();
  return { code, label, letter: /[A-Z]/.test(letter) ? letter : '#' };
};

export const mapCountries = (data: any): CountryOption[] => {
  const rows: CountryOption[] = [];
  const push = (item: any) => {
    const next = toCountry(item);
    if (next) rows.push(next);
  };
  if (Array.isArray(data)) data.forEach(push);
  else if (data && typeof data === 'object') {
    Object.values(data).forEach((group) => {
      if (Array.isArray(group)) group.forEach(push);
    });
  }
  if (rows.length) return rows;
  return FALLBACK_COUNTRIES.map((item) => toCountry(item)).filter(Boolean) as CountryOption[];
};

export const groupCountries = (list: CountryOption[], keyword: string) => {
  const text = keyword.trim().toLowerCase();
  const filtered = text
    ? list.filter((item) => item.label.toLowerCase().indexOf(text) >= 0 || item.code.toLowerCase().indexOf(text) >= 0)
    : list;
  const map = new Map<string, CountryOption[]>();
  filtered.forEach((item) => {
    const bucket = map.get(item.letter) || [];
    bucket.push(item);
    map.set(item.letter, bucket);
  });
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
};

const nonempty = (value: unknown) => String(value ?? '').trim();

const tradePermissionOf = (userInfo: any) => {
  const root = userInfo?.data ?? userInfo ?? {};
  const raw = root?.tradePermission;
  return raw && typeof raw === 'object' ? raw : {};
};

/**
 * 提交用当前户口。虚拟用加密户；证券 modeType 为 M 用融资户，否则用现金户。
 * 户号优先对 userInfo.tradePermission 的 marginAccountNum / cashAccountNum。
 */
export const pickSubUserId = (res: any) => {
  const accounts = overviewAccountsOf(res);
  const entry = getDepositEntry();
  if (entry.mode === 'virtual') {
    const crypto = accounts.find((row) => !isSecuritiesAccount(row));
    return crypto ? accountSubUserId(crypto) : '';
  }
  const wantMargin = entry.modeType === 'M';
  const perm = tradePermissionOf(userState.userInfo);
  const wantedNum = nonempty(wantMargin ? perm.marginAccountNum : perm.cashAccountNum);
  const securities = accounts.filter((row) => isSecuritiesAccount(row));
  const byNum = wantedNum ? securities.find((row) => accountNumberOf(row) === wantedNum) : null;
  const byType = securities.find((row) => String(row?.type ?? '').trim() === (wantMargin ? 'M' : 'C'));
  const hit = byNum || byType;
  return hit ? accountSubUserId(hit) : '';
};
